import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { get, getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './RechargeLogModal.module.css';

type RechargeLog = {
  id: number;
  orderNo: string;
  amount: number;
  amountBefore: number;
  amountAfter: number;
  type: number;
  description: string;
  createdAt: string;
  mergeCount?: number;
  mergedDetails?: RechargeLog[];
};

type CreditInfo = {
  balance: number;
  creditLimit: number;
};

const TYPE_INFO: Record<number, { label: string; cls: string }> = {
  [-2]: { label: '系统扣除', cls: 'typeDeduct' },
  [-1]: { label: '激活', cls: 'typeActivate' },
  1: { label: '充值', cls: 'typeRecharge' },
  2: { label: '赠送', cls: 'typeGift' },
  3: { label: '回收', cls: 'typeRefund' },
  4: { label: '收到转赠', cls: 'typeReceive' },
  5: { label: '注册赠送', cls: 'typeRegister' },
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proxyId: number;
  proxyName: string;
  balance?: number;
  onRecharge?: () => void;
};

const formatYuan = (n: number) =>
  `¥${Number(n || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const parseDescription = (desc: string): { gameName: string; code: string } => {
  const activation = desc.match(/\[([^\]]*)\]\s*[:：]\s*([\s\S]*)$/);
  if (activation) {
    return { gameName: activation[1], code: activation[2] };
  }
  const refund = desc.match(/[:：]\s*([^[]*?)\s*\[([^\]]*)\]\s*$/);
  if (refund) {
    return { gameName: refund[2].replace(/\s*\([^)]*\)\s*$/, ''), code: refund[1] };
  }
  return { gameName: '', code: desc };
};

const GAME_COLORS = ['#1677ff', '#7257e8', '#ff9d27', '#e5484d', '#20a9b5'];

type FilterKey = string;

const FILTERS: { key: FilterKey; label: string; types: number[] | 'all' }[] = [
  { key: 'all', label: '全部', types: 'all' },
  { key: 'recharge', label: '充值', types: [1] },
  { key: 'activate', label: '激活', types: [-1] },
  { key: 'recycle', label: '回收', types: [3] },
  { key: 'deduct', label: '系统扣除', types: [-2] },
  { key: 'gift', label: '赠送', types: [2, 5] },
];

const MERGEABLE_TYPES = [-1, 3];

const shouldMerge = (types: number[] | 'all') =>
  types === 'all' || types.some((type) => !MERGEABLE_TYPES.includes(type));

const colorOfName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = hash * 31 + name.charCodeAt(i);
  }
  const index = Math.abs(hash) % GAME_COLORS.length;
  return GAME_COLORS[index];
};

export const RechargeLogModal = ({ open, onOpenChange, proxyId, proxyName, balance, onRecharge }: Props) => {
  const [logs, setLogs] = useState<RechargeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<RechargeLog | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [refreshingCredit, setRefreshingCredit] = useState(false);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const copyTimer = useRef<number>();
  const balanceRefreshTimer = useRef<number>();
  const logsRequestRef = useRef(0);

  const handleRefreshCredit = async () => {
    if (refreshingCredit) {
      return;
    }
    setRefreshingCredit(true);
    setError(null);
    try {
      const data = await get<CreditInfo>(`/admin/tel/proxy/credit/refresh?proxyId=${proxyId}`);
      setCreditInfo({
        balance: Number(data?.balance) || 0,
        creditLimit: Number(data?.creditLimit) || 0,
      });
      setBalanceRefreshing(true);
      window.clearTimeout(balanceRefreshTimer.current);
      balanceRefreshTimer.current = window.setTimeout(() => setBalanceRefreshing(false), 600);
    } catch (e) {
      setError(e instanceof Error ? e.message : '刷新失败');
    } finally {
      setRefreshingCredit(false);
    }
  };

  const handleCopyCdk = async (id: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedId(id);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopiedId(null), 1800);
  };

  useEffect(
    () => () => {
      window.clearTimeout(copyTimer.current);
      window.clearTimeout(balanceRefreshTimer.current);
    },
    []
  );

  const fetchLogs = useCallback(
    async (filterKey: FilterKey) => {
      const requestId = ++logsRequestRef.current;
      setLoading(true);
      setError(null);
      try {
        const active = FILTERS.find((item) => item.key === filterKey) ?? FILTERS[0];
        const typesParam =
          active.types === 'all' ? '' : `&types=${active.types.join(',')}`;
        const mergeParam = shouldMerge(active.types) ? '' : '&merge=false';
        const result = await getPage<RechargeLog>(
          `/admin/tel/recharge/logs?proxyId=${proxyId}&limit=1000${typesParam}${mergeParam}`
        );
        if (requestId !== logsRequestRef.current) {
          return;
        }
        setLogs(result.records);
      } catch (e) {
        if (requestId !== logsRequestRef.current) {
          return;
        }
        setError(e instanceof Error ? e.message : '加载失败');
      } finally {
        if (requestId === logsRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [proxyId]
  );

  const fetchCredit = useCallback(async () => {
    try {
      const data = await get<CreditInfo>(`/admin/tel/proxy/credit?proxyId=${proxyId}`);
      setCreditInfo({
        balance: Number(data?.balance) || 0,
        creditLimit: Number(data?.creditLimit) || 0,
      });
    } catch {
      // 读取失败时保持不显示
    }
  }, [proxyId]);

  useEffect(() => {
    if (open) {
      setDetailTarget(null);
      setFilter('all');
      setCreditInfo(null);
      fetchLogs('all');
      fetchCredit();
    }
  }, [open, fetchLogs, fetchCredit]);

  const handleFilterChange = (key: FilterKey) => {
    if (key === filter) {
      return;
    }
    setFilter(key);
    fetchLogs(key);
  };

  const title = proxyName || `代理 #${proxyId}`;
  const currentBalance = logs.length > 0 ? Number(logs[0].amountAfter) : Number(balance || 0);
  const displayBalance = creditInfo ? creditInfo.balance : currentBalance;
  const displayCredit = creditInfo ? creditInfo.creditLimit : null;

  const openDetail = (log: RechargeLog) =>
    setDetailTarget({
      ...log,
      mergedDetails:
        log.mergedDetails && log.mergedDetails.length > 0 ? log.mergedDetails : [log],
    });

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>
            <span className={styles.bannerTitleAvatar}>
              {firstChineseChar(title)}
            </span>
            <span className={styles.bannerTitleName}>{title}</span>
            <span className={styles.bannerTitleSuffix}>余额记录</span>
          </div>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardLeft}>
              <span className={styles.balanceCardIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 7a2 2 0 0 1 2-2h12.5a1.5 1.5 0 0 1 1.5 1.5V8" />
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3" />
                  <path d="M21 11h-3.5a2.5 2.5 0 0 0 0 5H21z" />
                </svg>
              </span>
              <div className={`${styles.balanceCardInfo} ${balanceRefreshing ? styles.balanceRefreshing : ''}`}>
                <span className={styles.balanceCardHead}>
                  <span className={styles.balanceCardLabel}>余额</span>
                  <button
                    type="button"
                    className={styles.creditRefreshBtn}
                    disabled={refreshingCredit}
                    onClick={handleRefreshCredit}
                    aria-label="刷新额度"
                  >
                    <svg
                      className={refreshingCredit ? 'card-refresh-spin' : ''}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                      <path d="M21 3v5h-5" />
                    </svg>
                  </button>
                </span>
                <span className={styles.balanceCardValue}>
                  {formatYuan(displayBalance)}
                  <span className={styles.creditValue}>
                    （
                    {displayCredit != null
                      ? Number(displayCredit).toLocaleString('zh-CN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '--'}
                    ）
                  </span>
                </span>
              </div>
            </div>
            {onRecharge && (
              <div className={styles.balanceCardActions}>
                <button type="button" className={styles.balanceCardBtn} onClick={onRecharge}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M8.5 7.5 12 12l3.5-4.5" />
                    <path d="M12 12v5" />
                    <path d="M9 14.2h6M9 16.4h6" />
                  </svg>
                  充值
                </button>
              </div>
            )}
          </div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        <div className={styles.filterBar}>
          {FILTERS.map((item) => (
            <button
              key={String(item.key)}
              type="button"
              className={`${styles.filterChip} ${filter === item.key ? styles.filterChipActive : ''}`}
              onClick={() => handleFilterChange(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className={styles.bodyScroll}>
          {loading ? (
            <div className={styles.loading}>
              <span className="spinner" />
            </div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : logs.length > 0 ? (
            <div className={styles.list}>
              {logs.map((log) => {
                const info = TYPE_INFO[log.type] ?? { label: '其他', cls: '' };
                const isConsume = Number(log.type) < 0;
                const mergeable = Number(log.type) === -1 || Number(log.type) === 3;
                const folded = mergeable && (log.mergeCount ?? 1) > 1;
                return (
                  <div
                    key={log.id}
                    className={`${styles.item} ${folded ? styles.itemClickable : ''}`}
                    onClick={folded ? () => openDetail(log) : undefined}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemLeft}>
                        <span className={`${styles.amount} ${isConsume ? styles.amountConsume : ''}`}>
                          {isConsume ? '-' : '+'}
                          {formatYuan(log.amount)}
                        </span>
                        <span className={`${styles.typeTag} ${styles[info.cls] || ''}`}>{info.label}</span>
                        {folded && <span className={styles.mergeCount}>{log.mergeCount} 条</span>}
                      </span>
                      <span className={styles.time}>{log.createdAt}</span>
                    </div>
                    <div className={styles.itemBottom}>
                      <span className={styles.orderNo}>{log.orderNo}</span>
                      <span className={styles.after}>余额 {formatYuan(log.amountAfter)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.empty}>暂无记录</div>
          )}
        </div>
        <div className={styles.hint}>仅显示最近 1000 条记录</div>
      </div>
      {detailTarget && (
        <Modal
          nested
          open
          onOpenChange={(next) => {
            if (!next) {
              setDetailTarget(null);
            }
          }}
          className="app-modal"
          header={
            <div className={styles.banner}>
              <div className={styles.bannerTitle}>
                {(TYPE_INFO[detailTarget.type]?.label ?? '')}记录
              </div>
              <div className={styles.bannerSub}>
                共 {detailTarget.mergeCount} 条 · 合计 {formatYuan(detailTarget.amount)}
              </div>
              <div className={styles.bannerBars} aria-hidden="true">
                <i /><i /><i />
              </div>
            </div>
          }
        >
          <div className={styles.detailList}>
            {(detailTarget.mergedDetails ?? []).map((detail) => {
              const { gameName, code } = parseDescription(detail.description ?? '');
              const isConsume = Number(detail.type) < 0;
              return (
                <div key={detail.id} className={styles.detailItem}>
                  <span
                    className={styles.detailAvatar}
                    style={{ background: colorOfName(gameName || '') }}
                  >
                    {firstChineseChar(gameName || '')}
                  </span>
                  <div className={styles.detailMain}>
                    <div className={styles.detailTop}>
                      <span className={styles.detailName}>{gameName || '—'}</span>
                      <span className={styles.detailTime}>{detail.createdAt}</span>
                    </div>
                    <div className={styles.detailBottom}>
                      <span className={`${styles.detailAmount} ${isConsume ? styles.amountConsume : ''}`}>
                        {isConsume ? '-' : '+'}
                        {formatYuan(detail.amount)}
                      </span>
                      {code && (
                        <button
                          type="button"
                          className={styles.cdkBtn}
                          onClick={() => handleCopyCdk(detail.id, code)}
                        >
                          {copiedId === detail.id ? '已复制' : code}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </Modal>
    </>
  );
};
